import { getPostBySlug } from './ghost.js';

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function isValidUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

function getSlugFromPath() {
  const parts = window.location.pathname.replace(/\/$/, '').split('/');
  return parts[parts.length - 1];
}

function showLoading() {
  const container = document.getElementById('post-container');
  if (!container) return;
  container.innerHTML = `
    <div style="max-width: 760px; margin: 0 auto; padding: 120px 24px 80px;">
      <div style="display: flex; flex-direction: column; gap: 16px;">
        <div style="width: 80px; height: 14px; background: rgba(255,255,255,0.05); border-radius: 8px;"></div>
        <div style="width: 70%; height: 40px; background: rgba(255,255,255,0.06); border-radius: 8px;"></div>
        <div style="width: 90%; height: 20px; background: rgba(255,255,255,0.04); border-radius: 8px; margin-top: 8px;"></div>
        <div style="display: flex; align-items: center; gap: 12px; margin-top: 16px;">
          <div style="width: 44px; height: 44px; border-radius: 50%; background: rgba(255,255,255,0.05);"></div>
          <div style="display: flex; flex-direction: column; gap: 4px;">
            <div style="width: 100px; height: 14px; background: rgba(255,255,255,0.05); border-radius: 6px;"></div>
            <div style="width: 140px; height: 12px; background: rgba(255,255,255,0.04); border-radius: 6px;"></div>
          </div>
        </div>
      </div>
      <div style="margin-top: 48px; display: flex; flex-direction: column; gap: 16px;">
        <div style="width: 100%; height: 14px; background: rgba(255,255,255,0.04); border-radius: 6px;"></div>
        <div style="width: 95%; height: 14px; background: rgba(255,255,255,0.04); border-radius: 6px;"></div>
        <div style="width: 88%; height: 14px; background: rgba(255,255,255,0.04); border-radius: 6px;"></div>
        <div style="width: 100%; height: 14px; background: rgba(255,255,255,0.04); border-radius: 6px;"></div>
        <div style="width: 72%; height: 14px; background: rgba(255,255,255,0.04); border-radius: 6px;"></div>
      </div>
    </div>`;
}

function showNotFound() {
  const container = document.getElementById('post-container');
  if (!container) return;
  container.innerHTML = `
    <div style="text-align: center; padding-top: 60px; padding-bottom: 60px;">
      <h2 style="color: var(--color-text-primary); font-family: Sora, sans-serif; font-size: 28px; margin-bottom: 16px;">Post not found</h2>
      <p style="color: var(--color-text-muted); font-family: 'DM Sans', sans-serif;">The post you're looking for doesn't exist or has been removed.</p>
      <a href="/blog" style="color: var(--color-accent); font-family: 'DM Sans', sans-serif; margin-top: 24px; display: inline-block;">&larr; Back to blog</a>
    </div>`;
}

function showError() {
  const container = document.getElementById('post-container');
  if (!container) return;
  container.innerHTML = `
    <div style="text-align: center; padding-top: 60px; padding-bottom: 60px;">
      <h2 style="color: var(--color-text-primary); font-family: Sora, sans-serif; font-size: 28px; margin-bottom: 16px;">Something went wrong</h2>
      <p style="color: var(--color-text-muted); font-family: 'DM Sans', sans-serif;">We couldn't load this post. Please try again later.</p>
      <a href="/blog" style="color: var(--color-accent); font-family: 'DM Sans', sans-serif; margin-top: 24px; display: inline-block;">&larr; Back to blog</a>
    </div>`;
}

function renderPost(post) {
  const container = document.getElementById('post-container');
  if (!container) return;

  const titleText = escapeHtml(post.title);
  document.title = `${post.title} — ProductionReady`;

  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) ogTitle.setAttribute('content', post.title);
  const ogDesc = document.querySelector('meta[property="og:description"]');
  if (ogDesc && (post.custom_excerpt || post.excerpt)) ogDesc.setAttribute('content', post.custom_excerpt || post.excerpt);
  if (post.feature_image && isValidUrl(post.feature_image)) {
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) ogImage.setAttribute('content', post.feature_image);
  }

  const tags = (post.tags || [])
    .map((t) => `<span class="post-tag">${escapeHtml(t.name)}</span>`)
    .join('');

  const author = post.primary_author || {};
  const authorName = escapeHtml(author.name || 'ProductionReady');
  const authorAvatar =
    author.profile_image && isValidUrl(author.profile_image)
      ? `style="background-image: url('${encodeURI(author.profile_image)}'); background-size: cover;"`
      : '';

  const featureImageHtml =
    post.feature_image && isValidUrl(post.feature_image)
      ? `<figure class="post-feature-image"><img src="${encodeURI(post.feature_image)}" alt="${titleText}" /></figure>`
      : '';

  const excerptText = post.custom_excerpt || post.excerpt || '';

  container.style.opacity = '0';
  container.style.transition = 'opacity 0.3s ease';
  container.innerHTML = `
    <article class="post">
      <header class="post-header">
        ${tags ? `<div class="post-tags">${tags}</div>` : ''}
        <h1 class="post-title">${titleText}</h1>
        
        <div class="post-meta">
          <div class="post-author">
            <div class="post-author-avatar" ${authorAvatar}></div>
            <div class="post-author-info">
              <span class="post-author-name">${authorName}</span>
              <div class="post-meta-details">
                <time class="post-date" datetime="${escapeHtml(post.published_at)}">${escapeHtml(formatDate(post.published_at))}</time>
                ${post.reading_time ? `<span class="post-meta-separator">&middot;</span><span class="post-reading-time">${post.reading_time} min read</span>` : ''}
              </div>
            </div>
          </div>
        </div>
      </header>
      ${featureImageHtml}
      <div class="post-content">${post.html || ''}</div>
    </article>`;
  requestAnimationFrame(() => { container.style.opacity = '1'; });
}

async function init() {
  const slug = getSlugFromPath();
  if (!slug || slug === 'blog') {
    showNotFound();
    return;
  }

  showLoading();
  try {
    const post = await getPostBySlug(slug);
    renderPost(post);
  } catch (err) {
    console.error('Failed to fetch post:', err);
    const isNotFound =
      (err.type === 'NotFoundError') ||
      (err.context && /not found/i.test(err.context)) ||
      (err.message && /not found/i.test(err.message));
    if (isNotFound) {
      showNotFound();
    } else {
      showError();
    }
  }
}

init();
